/** The minimap.
 *
 * Two canvases: an offscreen one at exactly one texel per map cell, repainted
 * only when `obs.version` changes, and the visible one it is blown up into with
 * smoothing off. Separating them is what makes dragging the camera cheap - the
 * expensive pass over up to 20,736 cells does not run while the camera moves,
 * only the blit and the outline do.
 *
 * Sizing is done in *device* pixels, not CSS pixels.
 *
 * ## The shape of the well - the decision, and why
 *
 * The panel was left with a large dark region and an explicit note that the
 * fix wanted a decision rather than a drive-by: crop the well to the map's
 * aspect, or make the map fill a fixed square well. Both, and they are not
 * alternatives - they answer different halves of the same defect.
 *
 *  - The WELL takes the MAP's aspect (`aspectRatio` on the `Frame` below). A
 *    square well around a map that is not square is dead slate by
 *    construction, and no fit can rescue it. The map's proportions are the
 *    panel's proportions.
 *  - The MAP fills that well exactly - `fillMinimap`, not `fitMinimap`.
 *    Quantising to a whole pixel per cell sounds like the pixel-art answer and
 *    is the wrong one here, because the divisor is `floor(box / cells)` and the
 *    margin it leaves is therefore a sawtooth in the panel's width. Measured on
 *    the shipped 1080p shot the well is 243x243 device px for a 48-cell map:
 *    5.06 px per cell, so quantising costs 3 px. The same panel measured 239 px
 *    before the canvas was sized to the well, where the divisor drops to 4 and
 *    the cost is 47 px - a fifth of the panel - of slate inside a gilt frame. A
 *    margin that jumps by a fifth of the panel for a four-pixel change in
 *    window size is not a fit. The price of filling instead is that a
 *    nearest-neighbour upscale at 5.06 draws some cells 5 px and some 6 px;
 *    across flat colour regions that is invisible, and the alternative is not.
 *
 * The canvas is sized to the well in device pixels rather than to a rounded
 * multiple of the map, so the browser never rescales it a second time: at
 * 239x239 inside a 243x243 well the CSS stretch was resampling the blit.
 *
 * What is dark in the shipped shot after all that is not geometry. Over the
 * 243x243 well, 44,216 px - 74.9 % - are the unexplored slate (see `FOG_BLACK`
 * in `minimap-paint.ts`, which is keyed to the world's own shroud), and
 * the image reaches all four rails (its seating hairline is the well's own
 * first and last row and column). A day-one map is mostly unexplored, and
 * The classic's own minimap is black there too. Fog is information, not dead area.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { BitmapText } from "../theme/bitmapText.js";
import { Frame, Icon, Panel, useElementSize } from "../theme/components.js";
import { useObservation } from "../hooks/useGameState.js";
import {
  fillMinimap, minimapCameraRect, minimapCellAt, type CameraView, type MapExtent, type MinimapFit,
} from "./layout.js";
import { cameraAtLevel, levelIconKey, levelName, mapLevels } from "./level.js";
import {
  minimapBufferSize, minimapMarkers, paintMinimapMarkers, paintMinimapTerrain,
} from "./minimap-paint.js";

export interface MinimapProps {
  readonly camera: CameraView;
  readonly onCameraChange: (camera: CameraView) => void;
  /** Which underground level to show; defaults to the camera's. */
  readonly level?: number | undefined;
  readonly className?: string | undefined;
}

const CAMERA_STROKE = "#f4e0a3";
const CAMERA_SHADOW = "rgba(0,0,0,0.75)";
/**
 * The map's extent is the edge of the well, so the only thing drawn at the
 * image's border is a seating line: a dark hairline so the map does not bleed
 * into the frame's own gilt bead. The gilt itself belongs to the frame, and
 * drawing a second gold rectangle inside it was what made the panel read as
 * three concentric empty boxes.
 */
const MAP_EDGE_DARK = "rgba(0,0,0,0.75)";

export function Minimap(props: MinimapProps): JSX.Element {
  const obs = useObservation();
  const level = Math.trunc(props.level ?? props.camera.z);
  const { ref: boxRef, width, height } = useElementSize<HTMLDivElement>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sourceRef = useRef<HTMLCanvasElement | null>(null);

  const dpr = typeof window === "undefined" ? 1 : Math.max(1, Math.round(window.devicePixelRatio));
  const mapW = obs.map.width;
  const mapH = obs.map.height;

  const fit = useMemo<MinimapFit>(() => {
    const boxW = Math.max(1, Math.floor(width * dpr));
    const boxH = Math.max(1, Math.floor(height * dpr));
    const base = fillMinimap(mapW, mapH, boxW, boxH);
    // The well already carries the map's aspect, so the contain-fit above can
    // only be a rounding pixel short of the box. Spend it: draw the image at
    // the box's own size and drop the centring offsets, so the canvas backing
    // store is the well and no CSS rescale follows the blit. `cellPx` stays
    // the fitted one - it is what `minimapCellAt` inverts - and over a whole
    // map edge the two disagree by less than a pixel.
    return { cellPx: base.cellPx, offsetX: 0, offsetY: 0, drawWidth: boxW, drawHeight: boxH };
  }, [mapW, mapH, width, height, dpr]);

  // The terrain pass must not re-run when the camera moves, so the two values it
  // needs at the end of the pass are read through refs rather than deps.
  const fitRef = useRef(fit);
  fitRef.current = fit;
  const cameraRef = useRef(props.camera);
  cameraRef.current = props.camera;

  // ---- pass 1: terrain + markers, once per version.
  useEffect(() => {
    if (mapW === 0 || mapH === 0) return;
    let source = sourceRef.current;
    if (source === null || source.width !== mapW || source.height !== mapH) {
      source = document.createElement("canvas");
      source.width = mapW;
      source.height = mapH;
      sourceRef.current = source;
    }
    const ctx = source.getContext("2d");
    if (ctx === null) return;
    const image = ctx.createImageData(mapW, mapH);
    const buffer = image.data;
    if (buffer.length !== minimapBufferSize(mapW, mapH)) return;
    paintMinimapTerrain(obs, level, buffer);
    paintMinimapMarkers(minimapMarkers(obs, level), mapW, mapH, buffer);
    ctx.putImageData(image, 0, 0);
    compose(canvasRef.current, source, fitRef.current, cameraRef.current);
  }, [obs, level, mapW, mapH]);

  // ---- pass 2: blit + camera outline, on every camera or size change.
  useEffect(() => {
    compose(canvasRef.current, sourceRef.current, fit, props.camera);
  }, [fit, props.camera]);

  // ---- pointer: the panel owns events inside its own bounds (D8), and does
  //      not stop propagation - nothing above it is listening.
  const moveCameraTo = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const px = ((clientX - rect.left) / rect.width) * fit.drawWidth;
    const py = ((clientY - rect.top) / rect.height) * fit.drawHeight;
    const cell = minimapCellAt(fit, px, py, mapW, mapH);
    props.onCameraChange({
      ...props.camera,
      x: cell.x - props.camera.widthCells / 2,
      y: cell.y - props.camera.heightCells / 2,
      z: level,
    });
  }, [fit, mapW, mapH, props, level]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    moveCameraTo(e.clientX, e.clientY);
  }, [moveCameraTo]);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.buttons === 0) return;
    moveCameraTo(e.clientX, e.clientY);
  }, [moveCameraTo]);

  const extent = useMemo<MapExtent>(
    () => ({ width: mapW, height: mapH, levels: obs.map.levels }),
    [mapW, mapH, obs.map.levels],
  );
  const goToLevel = useCallback((z: number): void => {
    props.onCameraChange(cameraAtLevel(props.camera, extent, z));
  }, [props, extent]);

  return (
    <Panel className={props.className} style={{ padding: "var(--hm-pad)", flex: "0 0 auto" }}>
      {/* The well takes the MAP's aspect, not a square, and carries no padding:
          the frame's own gilt bead is then the map's extent and there is no
          slate margin between the two for the eye to read as dead area. See
          the decision at the head of this file. */}
      <Frame
        well
        className="hm-adv-minimap"
        style={{ padding: 0, aspectRatio: `${mapW || 1} / ${mapH || 1}` }}
      >
        <div ref={boxRef} style={{ width: "100%", height: "100%" }}>
          <canvas
            ref={canvasRef}
            className="hm-minimap"
            style={{ width: "100%", height: "100%", display: "block" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            aria-label="Minimap"
          />
        </div>
      </Frame>
      <LevelSwitch level={level} levels={mapLevels(extent)} onLevel={goToLevel} />
    </Panel>
  );
}

/**
 * Surface / underground, under the minimap - which is where the classic puts it and
 * the only place in the column where "which half of the world am I looking at"
 * belongs, because the image directly above it is the answer.
 *
 * It is drawn even when the level it offers is unexplored: the map has two
 * halves whether or not the player has been down, and a control that appears
 * only after you have already found the way down cannot teach anybody that
 * there is a way down. On a one-level map there is nothing to switch and the
 * whole row is absent rather than disabled.
 *
 * The current level is stated three ways because one is not enough on a
 * 174-design-pixel column: the plate is held pressed, its label goes from dim
 * to gold, and the label itself names the level rather than the direction.
 */
function LevelSwitch(props: {
  readonly level: number;
  readonly levels: readonly number[];
  readonly onLevel: (z: number) => void;
}): JSX.Element | null {
  if (props.levels.length < 2) return null;
  return (
    <div
      className="hm-adv-levels"
      role="group"
      aria-label="Map level"
      // The column count is the only thing here that depends on the data, so it
      // is the only thing left inline; the rest of the row is in `material.ts`.
      // `auto` tracks, not equal ones: the free space is shared equally
      // between content-sized keys, so `Surface` and `Underground` get the
      // same inset either side of their word. With equal tracks on the 174
      // device px sidebar of a 1366x768 window, `Underground` stood 1-2
      // device px from its bevel while `Surface` had 17.
      style={{ gridTemplateColumns: `repeat(${String(props.levels.length)}, auto)` }}
    >
      {props.levels.map((z) => {
        const here = z === props.level;
        return (
          <button
            key={z}
            type="button"
            className="hm-adv-key hm-adv-level"
            aria-pressed={here}
            title={`${levelName(z)} (u switches)`}
            onClick={() => { props.onLevel(z); }}
          >
            {/* Screened onto the key's own bronze face. The control icons are
                authored full-bleed on an opaque black ground like every other
                `ui.*` frame, so unblended each of these two keys carried a
                22x22 black square in the middle of a lit plate. */}
            <Icon atlasKey={levelIconKey(z)} size={22} className="hm-relief__inlay" />
            {/* Stamped, at magnification 1. In the kit's serif at 12px
                `UNDERGROUND` filled its plate to within 7px of both rims while
                `SURFACE` left 29px, so the pair read as one wide plate beside
                one narrow one even though the two are the same size. A bitmap
                mark at this step is 66 and 41 glyph px in a 123px plate, which
                leaves both of them air. */}
            <span className="hm-adv-key__label">
              <BitmapText face="roman" scale={1}>{levelName(z)}</BitmapText>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Blit the cell-resolution source up to the panel and outline the camera. */
function compose(
  canvas: HTMLCanvasElement | null,
  source: HTMLCanvasElement | null,
  fit: MinimapFit,
  camera: CameraView,
): void {
  if (canvas === null || source === null) return;
  if (fit.drawWidth <= 0 || fit.drawHeight <= 0) return;
  if (canvas.width !== fit.drawWidth) canvas.width = fit.drawWidth;
  if (canvas.height !== fit.drawHeight) canvas.height = fit.drawHeight;
  const ctx = canvas.getContext("2d");
  if (ctx === null) return;

  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, source.width, source.height, 0, 0, fit.drawWidth, fit.drawHeight);

  // `fillMinimap` centres inside the box, but the canvas is sized to the image,
  // so the offsets are zero here; ask for the rect with a zero-offset fit.
  ctx.lineWidth = 1;
  ctx.strokeStyle = MAP_EDGE_DARK;
  ctx.strokeRect(0.5, 0.5, fit.drawWidth - 1, fit.drawHeight - 1);

  const rect = minimapCameraRect({ ...fit, offsetX: 0, offsetY: 0 }, camera);
  // Half-pixel inset so a 1px stroke lands on the pixel, not between two.
  const x = rect.x + 0.5;
  const y = rect.y + 0.5;
  const w = Math.max(1, Math.min(fit.drawWidth - 1, rect.w - 1));
  const h = Math.max(1, Math.min(fit.drawHeight - 1, rect.h - 1));

  ctx.lineWidth = 1;
  ctx.strokeStyle = CAMERA_SHADOW;
  ctx.strokeRect(x - 1, y - 1, w + 2, h + 2);
  ctx.strokeStyle = CAMERA_STROKE;
  ctx.strokeRect(x, y, w, h);
}
